import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CalendarEvent, DAYS_OF_WEEK, CalendarDateFormatter, CalendarView } from 'angular-calendar';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SessionService } from 'src/app/shared/services/session.service';
import { CustomDateFormatter } from 'src/app/shared/utilities/custom-date-formatter.provider';
import { CalendarDay } from '../../models/calendario';
import { AgendaService } from '../../services/agenda.service';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { IFiltrosAgenda } from '../../models/agenda';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ESolicitacaoConsultaStatus, IConsulta } from 'src/app/pages/consultas/data/consultas';
import { DoctorService } from 'src/app/pages/doctors/services/doctor.service';
import { IListByFilterMedico, IMedico } from 'src/app/pages/doctors/models/doctor';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { UsuarioQuickblox } from '../../models/quickblox';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';

@Component({
  selector: 'app-agenda',
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.scss'],
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter
    }
  ]
})
export class AgendaComponent implements OnInit, OnDestroy {
  @Input() formModalConsulta!: UntypedFormGroup;
  doctorControl = new UntypedFormControl('');

  showModalDetalhesConsulta = false;
  showModalMarcarConsulta = false;
  marcarConsultaDate?: Date;

  primeiroDiaSemana = new Date();
  viewDate = new Date();
  ultimoDiaSemana = new Date();
  currentDate = new Date();

  view: CalendarView = CalendarView.Week;
  events$!: Observable<CalendarEvent[]>;
  locale: string = 'pt-BR';
  weekStartsOn: number = DAYS_OF_WEEK.SUNDAY;
  weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];

  calendarDate = new Date();
  calendarEvents$!: Observable<CalendarDay[]>;

  diasSemana: string[] = [];
  consulta!: IConsulta;

  doctors: IMedico[] = [];
  isDoctor = true;

  consultasDia: IConsulta[] = [];

  eventsInterval: any;

  constructor(
    private readonly agendaService: AgendaService,
    private readonly sessionService: SessionService,
    private readonly doctorService: DoctorService,
    private readonly loadingService: LoadingService,
    private readonly toastService: ToastService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isDoctor = this.sessionService.getSession().role === ERoleAdminString.Doctor;

    this.carregarAgendamentos();
    this.carregarMedicos();

    this.eventsInterval = setInterval(() => {
      this.loadingService.setSkipNext('MedicalAppointment/ListByFilterAsync');
      this.carregarAgendamentos();
    }, 1000 * 60 * 10); //a cada 10 minutos atualiza a agenda
  }

  ngOnDestroy(): void {
    clearInterval(this.eventsInterval);
  }

  carregarAgendamentos(date?: Date): void {
    if (date) {
      this.viewDate = date;
    }

    this.getConsultasCalendarioByData(this.viewDate);

    this.primeiroDiaSemana = startOfWeek(this.viewDate);
    this.ultimoDiaSemana = endOfWeek(this.viewDate);

    this.calcularDiasSemana();

    this.primeiroDiaSemana.setDate(this.primeiroDiaSemana.getDate() - 1);
    this.primeiroDiaSemana.setHours(21, 0, 0);
    this.ultimoDiaSemana.setHours(20, 59, 59);

    this.events$ = this.agendaService
      .carregarAgendaMedico(this.getFilters(this.primeiroDiaSemana, this.ultimoDiaSemana))
      .pipe(
        map((res) => {
          if (res.isSucceed) {
            this.carregarConsultasDia(res.data);

            return res.data
              .filter((consulta) => consulta.status !== ESolicitacaoConsultaStatus.cancelada)
              .map((consulta: IConsulta) => {
                const horaComecoConsulta = new Date(consulta.appointmentDate);

                return this.agendaService.createScheduleEvent(
                  horaComecoConsulta,
                  consulta.duration,
                  consulta.employee?.name!,
                  consulta.status!,
                  consulta.modality,
                  consulta.id
                );
              });
          } else {
            return [];
          }
        })
      );

    this.changeDetectorRef.detectChanges();
  }

  calcularDiasSemana(): void {
    const primeiroDiaSemana = startOfWeek(this.viewDate);
    this.diasSemana = [];
    for (let i = 0; i < 7; i++) {
      const dia = new Date(primeiroDiaSemana);
      dia.setDate(dia.getDate() + i);

      const diaString = format(dia, 'dd', { locale: ptBR });

      this.diasSemana.push(diaString);
    }
  }

  showModalConsulta(id: string): void {
    this.agendaService.carregarConsultaId(id).subscribe((dadosConsulta) => {
      this.consulta = dadosConsulta.data;
      this.showModalDetalhesConsulta = true;
    });
  }

  scheduleConsulta(event: Date): void {
    const timeDifference = event.getTime() - new Date().getTime();

    // const fourHoursMilliseconds = 240 * 60 * 1000;

    if (0 > timeDifference) {
      this.toastService.toast('Não é possível agendar uma consulta no passado', EMessageType.Warning);
      // } else if (timeDifference < fourHoursMilliseconds) {
      //   this.toastService.toast(
      //     'Agendamento só pode ocorrer com pelo menos 4 horas de antecedência',
      //     EMessageType.Warning
      //   );
    } else {
      this.marcarConsultaDate = event;
      this.showModalMarcarConsulta = true;
    }
  }

  enterConsulta(appointmentId: string): void {
    const consulta = this.consultasDia.find((consulta) => consulta.id === appointmentId);

    if (consulta) {
      combineLatest([
        this.agendaService.carregarUsuarioQuickblox(consulta.employeeId),
        this.agendaService.carregarUsuarioQuickblox(consulta.doctorId!)
      ])
        .pipe(
          switchMap(([pacienteResult, doctorResult]) =>
            combineLatest([
              this.getQuickbloxUserObservable(pacienteResult.data, consulta.employeeId, consulta.employee?.name!),
              this.getQuickbloxUserObservable(doctorResult.data, consulta.doctorId!, consulta.doctor?.name!)
            ])
          ),
          take(1)
        )
        .subscribe(([paciente, doctor]) => {
          console.log(consulta, paciente, doctor);
          window.open(
            `http://localhost:4200/doctor?login=${doctor?.login}&password=${doctor?.password}&doctorId=${
              doctor?.userQuickBlox
            }&patientId=${
              paciente?.userQuickBlox
            }&appointmentId=${appointmentId}&token=${this.sessionService.getToken()}`,
            '_blank'
          );
        });
    }
  }

  hideModalConsulta(): void {
    this.showModalDetalhesConsulta = false;
  }

  hideModalMarcarConsulta(): void {
    this.showModalMarcarConsulta = false;
    this.carregarAgendamentos();
  }

  getAgendamentosByDate(date: Date): void {
    this.viewDate = date;
    this.carregarAgendamentos();
  }

  getConsultasCalendarioByData(data: Date): void {
    this.calendarDate = data;

    const filtro = this.getFilters(
      new Date(data.getFullYear(), data.getMonth(), 1),
      new Date(data.getFullYear(), data.getMonth() + 1, 0, 20)
    );

    this.calendarEvents$ = this.agendaService.carregarAgendaMedico(filtro).pipe(
      map((res) => {
        if (res.isSucceed) {
          return res.data
            .filter((consulta) => consulta.status !== ESolicitacaoConsultaStatus.cancelada)
            .map(
              (consulta) =>
                ({
                  textColor: 'light',
                  backgroundColor: this.agendaService.getCalendarColorByStatus(consulta.status),
                  dayNumber: new Date(consulta.appointmentDate).getDate()
                } as CalendarDay)
            );
        } else {
          return [];
        }
      })
    );

    this.changeDetectorRef.detectChanges();
  }

  checkIfDayIsInCurrentWeek(): boolean {
    const inicioSemana = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      this.currentDate.getDate() - this.currentDate.getDay()
    );

    const fimSemana = new Date(inicioSemana.getFullYear(), inicioSemana.getMonth(), inicioSemana.getDate() + 6);

    return this.viewDate.getTime() > inicioSemana.getTime() && this.viewDate.getTime() < fimSemana.getTime();
  }

  private getFilters(periodStart: Date, periodEnd: Date): IFiltrosAgenda {
    const filter: IFiltrosAgenda = {
      periodStart: periodStart.toISOString(),
      periodEnd: periodEnd.toISOString()
    };

    const companyId = this.sessionService.getSession().companyId;
    const partnerId = this.sessionService.getSession().partnerId;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filter.partnerId = partnerId;
    }

    if (companyId) {
      filter.companyId = companyId;
    }

    if (accountRole) {
      filter.accountRole = parseInt(accountRole);
    }

    if (this.isDoctor) {
      filter.doctorId = this.sessionService.getSession().sid;
    } else if (this.doctorControl.value) {
      filter.doctorId = this.doctorControl.value;
    }

    return filter;
  }

  private carregarMedicos(): void {
    if (!this.isDoctor) {
      const partnerId = this.sessionService.getSession().partnerId;
      const accountRole = this.sessionService.getSession().accountRole;
      const companyId = this.sessionService.getSession().companyId;

      const filtros: IListByFilterMedico = {
        pageNumber: 0,
        pageSize: 9999,
        orderBy: EOrdenacao.AlfabeticaAZ,
        active: true
      };

      if (accountRole) {
        filtros.accountRole = parseInt(accountRole);
      }

      if (partnerId) {
        filtros.partnerId = partnerId;
      }

      if (companyId) {
        filtros.companyId = companyId;
      }

      this.doctorService
        .listarTodosMedicos(filtros)
        .pipe(take(1))
        .subscribe((res) => {
          if (res.isSucceed) {
            this.doctors = res.data;
          }
        });
    }

    this.doctorControl.valueChanges.subscribe(() => {
      this.carregarAgendamentos();
    });
  }

  private carregarConsultasDia(consultas: IConsulta[]): void {
    const currentDate = new Date();
    if (consultas.length > 0) {
      this.consultasDia =
        consultas?.filter(
          (consulta) =>
            new Date(consulta.appointmentDate).getFullYear() === currentDate.getFullYear() &&
            new Date(consulta.appointmentDate).getMonth() === currentDate.getMonth() &&
            new Date(consulta.appointmentDate).getDate() === currentDate.getDate()
        ) ?? [];

      this.consultasDia = this.consultasDia.sort((a, b) => {
        if (a.appointmentDate > b.appointmentDate) return 1;
        else if (a.appointmentDate < b.appointmentDate) return -1;
        else return 0;
      });
    }
  }

  private get isDoctorSelected(): boolean {
    return this.isDoctor || !!this.doctorControl.value;
  }

  private getQuickbloxUserObservable(
    usuarioQuickblox: UsuarioQuickblox | undefined,
    id: string,
    name: string
  ): Observable<UsuarioQuickblox | undefined> {
    let user$: Observable<UsuarioQuickblox | undefined> = of(usuarioQuickblox);
    if (!usuarioQuickblox) {
      const newUser = this.agendaService.newUsuarioQuickblox(name);
      user$ = this.agendaService.createUsuarioQuickblox(newUser, id).pipe(
        map(
          (result) =>
            ({
              login: newUser.login,
              password: newUser.password,
              userQuickBlox: result.data.user.id
            } as UsuarioQuickblox)
        )
      );
    }

    return user$;
  }
}
